import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Settings as ImpersonateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RemoveRedEye as RemoveRedEyeIcon,
  SettingsBackupRestore as RestoreIcon,
  DoDisturb as UnVerifiedIcon,
  CheckCircleOutline as VerifiedIcon,
  PersonOutlined as EnableIcon,
  PersonOffOutlined as DisableIcon,
  Search as SearchIcon,
  GroupOutlined as GroupOutlinedIcon,
  PersonAddAlt1Outlined as PersonAddAlt1OutlinedIcon,
  HighlightOff as HighlightOffIcon,
} from "@mui/icons-material";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";

import useUser from "../../../../hooks/super/useUser";
import useAuth from "../../../../hooks/useAuth";

import UserModal from "./M_User";
import DeleteUserModal from "./M_DeleteUser";
import RestoreUserModal from "./M_RestoreUser";
import EnableUserModal from "./M_EnableUser";
import DisableUserModal from "./M_DisableUser";
import VerifyUserModal from "./M_VerifyUser";
import ViewUserModal from "./M_ViewUser";

import { formatDate, getUserRoleString } from "../../../../utils/format";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import { UserType } from "../../../../utils/constants";

import {
  TOKENS,
  StatTile,
  StatusPill,
  Monogram,
  RowMenu,
  EmptyState,
  TabSwitcher,
} from "../../../../components/pages/_new/super/design";

const TABS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "new", label: "New" },
  { value: "deleted", label: "Deleted" },
];

const headCells = [
  { id: "firstName", label: "User", sortable: true },
  { id: "role", label: "Role", sortable: true },
  { id: "title", label: "Title", sortable: true },
  { id: "isVerified", label: "Verified", sortable: true },
  { id: "enabled", label: "Status", sortable: true },
  { id: "created", label: "Created", sortable: true },
  { id: "action", label: "", sortable: false },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{ color: `${TOKENS.fg2} !important`, "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` } }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

const IdMono = ({ value }) => (
  <Typography
    sx={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 11,
      color: TOKENS.fg3,
    }}
  >
    {value}
  </Typography>
);

const VerifiedDot = ({ value }) =>
  value ? (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <VerifiedIcon sx={{ fontSize: 16, color: TOKENS.successInk }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.successInk, fontWeight: 500 }}>Verified</Typography>
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <UnVerifiedIcon sx={{ fontSize: 16, color: TOKENS.fg3 }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>Unverified</Typography>
    </Stack>
  );

function UserTable() {
  const navigate = useNavigate();
  const { viewUser, impersonateUser, users } = useUser();
  const { adminUserType, setImpersonate, getFeatures } = useAuth();

  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("created");
  const [search, setSearch] = React.useState("");
  const [tab, setTab] = React.useState("all");

  // modal state
  const [open, setOpen] = React.useState(false);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const [enableOpen, setEnableOpen] = React.useState(false);
  const [disableOpen, setDisableOpen] = React.useState(false);
  const [verifyOpen, setVerifyOpen] = React.useState(false);
  const [removeFlag, setRemoveFlag] = React.useState(false);

  const [uid, setUid] = React.useState(0);
  const [email, setEmail] = React.useState();
  const [userName, setUserName] = React.useState([]);
  const [user, setUser] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const handleSort = (id) => {
    if (id === orderBy) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const prepareAction = (row) => {
    setUserName([{ id: row.id, email: row.email }]);
  };

  const onView = async (row) => {
    setUid(row.id);
    setLoading(true);
    setViewOpen(true);
    const u = await viewUser(row.id);
    setUser(u);
    setLoading(false);
  };
  const onEdit = async (row) => {
    setUid(row.id);
    setLoading(true);
    setOpen(true);
    const u = await viewUser(row.id);
    setUser(u);
    setLoading(false);
  };
  const onVerify = (row) => {
    setUid(row.id);
    setEmail(row.email);
    prepareAction(row);
    setVerifyOpen(true);
  };
  const onDelete = (row) => {
    setUid(row.id);
    setRemoveFlag(false);
    prepareAction(row);
    setDeleteOpen(true);
  };
  const onRemove = (row) => {
    setUid(row.id);
    setRemoveFlag(true);
    prepareAction(row);
    setDeleteOpen(true);
  };
  const onRestore = (row) => {
    setUid(row.id);
    prepareAction(row);
    setRestoreOpen(true);
  };
  const onEnable = (row) => {
    setUid(row.id);
    prepareAction(row);
    setEnableOpen(true);
  };
  const onDisable = (row) => {
    setUid(row.id);
    prepareAction(row);
    setDisableOpen(true);
  };
  const onImpersonate = async (row) => {
    const u = await impersonateUser(row.email);
    await setImpersonate(u);
    await getFeatures();
    navigate("/v2/overview");
  };

  // counts for stat tiles + tab badges
  const counts = React.useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    let total = list.length;
    let active = 0;
    let newCount = 0;
    let deleted = 0;
    list.forEach((u) => {
      if (u?.isDeleted) {
        deleted += 1;
      } else if (!u?.isVerified) {
        newCount += 1;
      } else {
        active += 1;
      }
    });
    return { total, active, new: newCount, deleted };
  }, [users]);

  // tab filter
  const tabFiltered = React.useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    switch (tab) {
      case "active":
        return list.filter((u) => !u?.isDeleted && u?.isVerified);
      case "new":
        return list.filter((u) => !u?.isDeleted && !u?.isVerified);
      case "deleted":
        return list.filter((u) => u?.isDeleted);
      case "all":
      default:
        return list;
    }
  }, [users, tab]);

  // search
  const filtered = React.useMemo(() => {
    if (!search) return tabFiltered;
    const q = search.toLowerCase();
    return tabFiltered.filter((row) => {
      const fullName = `${row?.firstName || ""} ${row?.lastName || ""}`.toLowerCase();
      return (
        (row?.email || "").toLowerCase().includes(q) ||
        fullName.includes(q) ||
        (row?.title || "").toLowerCase().includes(q) ||
        String(row?.id || "").toLowerCase().includes(q)
      );
    });
  }, [tabFiltered, search]);

  const sorted = React.useMemo(() => stableSort(filtered, getComparator(order, orderBy)), [filtered, order, orderBy]);

  const isLoading = users === null;
  const canManage = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  return (
    <Box>
      {/* Summary tiles */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatTile
          icon={<GroupOutlinedIcon />}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.total}
          label="Total users"
        />
        <StatTile
          icon={<VerifiedIcon />}
          iconBg={TOKENS.successBg}
          iconInk={TOKENS.successInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.active}
          label="Active"
        />
        <StatTile
          icon={<PersonAddAlt1OutlinedIcon />}
          iconBg={TOKENS.infoBg}
          iconInk={TOKENS.infoInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.new}
          label="New (unverified)"
        />
        <StatTile
          icon={<HighlightOffIcon />}
          iconBg={TOKENS.dangerBg}
          iconInk={TOKENS.dangerInk}
          value={isLoading ? <Skeleton width={48} sx={{ display: "inline-block" }} /> : counts.deleted}
          label="Deleted"
        />
      </Box>

      {/* Main panel */}
      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        {/* Tabs */}
        <Box sx={{ px: "16px", pt: "12px" }}>
          <TabSwitcher
            value={tab}
            onChange={setTab}
            tabs={TABS.map((t) => ({
              value: t.value,
              label: t.label,
              count:
                t.value === "all"
                  ? counts.total
                  : t.value === "active"
                  ? counts.active
                  : t.value === "new"
                  ? counts.new
                  : counts.deleted,
            }))}
          />
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            p: "10px 16px 14px",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search by name, email, title, id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TOKENS.fg3 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3 }}>
            {isLoading ? "Loading..." : `${sorted.length} of ${tabFiltered.length}`}
          </Typography>
        </Box>

        <TableContainer>
          <Table aria-label="users table" sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                {headCells.map((c) => (
                  <HeaderCell
                    key={c.id}
                    id={c.id}
                    sortable={c.sortable}
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align={c.id === "action" ? "right" : "left"}
                  >
                    {c.label}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {headCells.map((__, j) => (
                        <DataCell key={`sk-${i}-${j}`}>
                          <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                        </DataCell>
                      ))}
                    </TableRow>
                  ))
                : sorted.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                        <EmptyState
                          icon={<GroupOutlinedIcon />}
                          title={
                            search
                              ? "No users match your search"
                              : tab === "deleted"
                              ? "No deleted users"
                              : tab === "new"
                              ? "No pending users"
                              : "No users yet"
                          }
                          body={
                            search
                              ? "Try a different keyword."
                              : tab === "all"
                              ? "Use the Add New User button to invite one."
                              : "Switch tabs or invite a new user."
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )
                : sorted.map((row) => {
                    const fullName =
                      `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.email || "—";
                    const isDeleted = row?.isDeleted;
                    const isEnabled = row?.enabled;
                    const status = isDeleted
                      ? "Suspended"
                      : isEnabled
                      ? row?.isVerified
                        ? "Active"
                        : "Pending"
                      : "Suspended";
                    const actions = [];
                    if (canManage) {
                      actions.push({
                        key: "impersonate",
                        icon: <ImpersonateIcon fontSize="small" />,
                        label: "Impersonate",
                        onClick: () => onImpersonate(row),
                      });
                    }
                    actions.push({
                      key: "view",
                      icon: <RemoveRedEyeIcon fontSize="small" />,
                      label: "View",
                      onClick: () => onView(row),
                    });
                    if (canManage) {
                      actions.push({
                        key: "edit",
                        icon: <EditIcon fontSize="small" />,
                        label: "Edit",
                        onClick: () => onEdit(row),
                      });
                      if (!row?.isVerified) {
                        actions.push({
                          key: "verify",
                          icon: <VerifiedIcon fontSize="small" />,
                          label: "Verify",
                          onClick: () => onVerify(row),
                        });
                      }
                      if (isEnabled) {
                        actions.push({
                          key: "disable",
                          icon: <DisableIcon fontSize="small" />,
                          label: "Disable",
                          onClick: () => onDisable(row),
                        });
                      } else {
                        actions.push({
                          key: "enable",
                          icon: <EnableIcon fontSize="small" />,
                          label: "Enable",
                          onClick: () => onEnable(row),
                        });
                      }
                      if (isDeleted) {
                        actions.push({
                          key: "restore",
                          icon: <RestoreIcon fontSize="small" />,
                          label: "Restore",
                          onClick: () => onRestore(row),
                        });
                      } else {
                        actions.push({
                          key: "delete",
                          icon: <DeleteIcon fontSize="small" />,
                          label: "Delete",
                          danger: true,
                          onClick: () => onDelete(row),
                        });
                      }
                      actions.push({ key: "div", divider: true });
                      actions.push({
                        key: "remove",
                        icon: <RemoveIcon fontSize="small" />,
                        label: "Remove permanently",
                        danger: true,
                        onClick: () => onRemove(row),
                      });
                    }
                    return (
                      <TableRow key={row?.id} hover sx={{ "&:hover": { background: TOKENS.bgSunken } }}>
                        <DataCell>
                          <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Monogram name={fullName} size={36} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13.5,
                                  fontWeight: 600,
                                  color: TOKENS.fg1,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {fullName}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: TOKENS.fg2 }}>{row?.email || "—"}</Typography>
                            </Box>
                          </Stack>
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>{getUserRoleString(row?.role) || "—"}</Typography>
                        </DataCell>
                        <DataCell>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>{row?.title || "—"}</Typography>
                        </DataCell>
                        <DataCell>
                          <VerifiedDot value={row?.isVerified} />
                        </DataCell>
                        <DataCell>
                          <StatusPill status={status} />
                        </DataCell>
                        <DataCell>
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
                              {row?.created ? formatDate(row.created) : "—"}
                            </Typography>
                            <IdMono value={row?.id} />
                          </Stack>
                        </DataCell>
                        <DataCell align="right">
                          <RowMenu actions={actions} />
                        </DataCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <UserModal open={open} handleClose={() => setOpen(false)} user={user} loading={loading} />
      <ViewUserModal open={viewOpen} handleClose={() => setViewOpen(false)} user={user} loading={loading} />
      <DeleteUserModal
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        uid={uid}
        userName={userName}
        removeFlag={removeFlag}
      />
      <RestoreUserModal
        open={restoreOpen}
        handleClose={() => setRestoreOpen(false)}
        uid={uid}
        userName={userName}
      />
      <EnableUserModal
        open={enableOpen}
        handleClose={() => setEnableOpen(false)}
        uid={uid}
        userName={userName}
      />
      <DisableUserModal
        open={disableOpen}
        handleClose={() => setDisableOpen(false)}
        uid={uid}
        userName={userName}
      />
      <VerifyUserModal
        open={verifyOpen}
        handleClose={() => setVerifyOpen(false)}
        email={email}
        userName={userName}
      />
    </Box>
  );
}

export default UserTable;
